import React, { useState, useEffect } from "react";
import axios from "axios";
import StoryCluster, { clusterFromBackend } from "./redesign/StoryCluster";
import {
  SectionHeader,
  SectionItem,
} from "./redesign/Sections";

const CategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [categoryData, setCategoryData] = useState({});
  const [topLajme, setTopLajme] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/categories`)
      .then((response) => {
        setCategories(response.data);
        fetchAllCategoryData(response.data);
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Freshness-weighted trending score (kept from original logic)
  const calculateTrendingScore = (clusterData) => {
    const articles = clusterData.articles || [];
    const articleCount = articles.length;
    if (articleCount === 0) return 0;

    const now = new Date();
    let totalFreshnessScore = 0;
    articles.forEach((article) => {
      if (article.fetch_date) {
        const articleDate = new Date(article.fetch_date);
        const hoursAgo = (now - articleDate) / (1000 * 60 * 60);
        const freshnessScore = Math.max(0, 36 - hoursAgo);
        totalFreshnessScore += freshnessScore;
      }
    });

    const avgFreshness = totalFreshnessScore / articleCount;
    return avgFreshness * 10 + articleCount * 3;
  };

  const fetchAllCategoryData = async (categoryList) => {
    const categoryDataMap = {};
    const allClusters = [];
    let completed = 0;

    const promises = categoryList.map(async (category) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/category/${category}`
        );
        const clusters = response.data;
        categoryDataMap[category] = clusters;

        Object.entries(clusters).forEach(([clusterId, clusterData]) => {
          allClusters.push({
            category,
            clusterId,
            clusterData,
            trendingScore: calculateTrendingScore(clusterData),
          });
        });

        completed++;
        setLoadingProgress(
          Math.round((completed / categoryList.length) * 100)
        );
        return { category, data: clusters };
      } catch (error) {
        console.error(`Error fetching data for category ${category}:`, error);
        categoryDataMap[category] = {};
        completed++;
        setLoadingProgress(
          Math.round((completed / categoryList.length) * 100)
        );
        return { category, data: {} };
      }
    });

    await Promise.all(promises);

    // Group by category and sort by trending score
    const clustersByCategory = {};
    allClusters.forEach((c) => {
      if (!clustersByCategory[c.category]) clustersByCategory[c.category] = [];
      clustersByCategory[c.category].push(c);
    });
    Object.keys(clustersByCategory).forEach((cat) => {
      clustersByCategory[cat].sort((a, b) => b.trendingScore - a.trendingScore);
    });

    const getTopFromCategory = (name) => {
      const list = clustersByCategory[name];
      return list && list.length > 0 ? list[0] : null;
    };

    // Top Lajme order: Shqipëri → Kosovë → Bota → max(Maqedoni, Sport)
    const topClusters = [];
    const shqiperiaTop = getTopFromCategory("Shqipëri");
    if (shqiperiaTop) topClusters.push(shqiperiaTop);
    const kosovaTop = getTopFromCategory("Kosovë");
    if (kosovaTop) topClusters.push(kosovaTop);
    const botaTop = getTopFromCategory("Bota");
    if (botaTop) topClusters.push(botaTop);

    const maqedoniaTop = getTopFromCategory("Maqedoni");
    const sportTop = getTopFromCategory("Sport");
    if (maqedoniaTop && sportTop) {
      const maq = maqedoniaTop.clusterData.articles?.length || 0;
      const spo = sportTop.clusterData.articles?.length || 0;
      topClusters.push(spo > maq ? sportTop : maqedoniaTop);
    } else if (maqedoniaTop) topClusters.push(maqedoniaTop);
    else if (sportTop) topClusters.push(sportTop);

    const topKeys = new Set(
      topClusters.map((c) => `${c.category}-${c.clusterId}`)
    );

    const filteredCategoryData = {};
    Object.entries(categoryDataMap).forEach(([category, clusters]) => {
      filteredCategoryData[category] = Object.entries(clusters)
        .filter(([cid]) => !topKeys.has(`${category}-${cid}`))
        .slice(0, 3);
    });

    setTopLajme(topClusters);
    setCategoryData(filteredCategoryData);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${loadingProgress}%` }}
          />
        </div>
      </div>
    );
  }

  const [hero, ...secondary] = topLajme;

  return (
    <div className="flex flex-col gap-10">
      {/* TOP LAJME */}
      {hero && (
        <section>
          <SectionHeader title="Top Lajme" eyebrow="Aktuale" />
          <StoryCluster
            cluster={clusterFromBackend(
              hero.category,
              hero.clusterId,
              hero.clusterData
            )}
            variant="hero"
            detailHref={`/category/${hero.category}/cluster/${hero.clusterId}`}
          />
          <div className="mt-2">
            {secondary.map((c) => (
              <StoryCluster
                key={`${c.category}-${c.clusterId}`}
                cluster={clusterFromBackend(
                  c.category,
                  c.clusterId,
                  c.clusterData
                )}
                variant="compact"
                detailHref={`/category/${c.category}/cluster/${c.clusterId}`}
              />
            ))}
          </div>
        </section>
      )}

      {/* CATEGORY SECTIONS — on mobile each category is a complete vertical
          block (header + items together). On md+ they pair into 2-column
          subgrids so row heights sync across columns. */}
      {categories.length === 0 ? (
        <p className="text-sub">Nuk ka kategori të disponueshme.</p>
      ) : (
        <div className="flex flex-col gap-10">
          {(() => {
            const pairs = [];
            for (let i = 0; i < categories.length; i += 2) {
              pairs.push(categories.slice(i, i + 2));
            }
            return pairs.map((pair, pi) => (
              <div
                key={`pair-${pi}`}
                className="grid grid-cols-1 gap-x-8 gap-y-10 xl:grid-cols-2 xl:grid-rows-[auto_auto_auto_auto] xl:gap-y-0"
              >
                {[0, 1].map((ci) => {
                  const cat = pair[ci];
                  if (!cat) return <div key={`empty-col-${ci}`} className="hidden xl:block" />;
                  const items = categoryData[cat] || [];
                  return (
                    <div
                      key={`col-${cat}`}
                      className="xl:row-span-4 xl:grid xl:grid-rows-subgrid"
                    >
                      <SectionHeader
                        title={cat}
                        viewAllHref={`/category/${cat}`}
                      />
                      {[0, 1, 2].map((idx) => {
                        const tuple = items[idx];
                        if (!tuple) {
                          return <div key={`empty-${cat}-${idx}`} />;
                        }
                        const [clusterId, clusterData] = tuple;
                        return (
                          <SectionItem
                            key={`${cat}-${clusterId}`}
                            clusterId={clusterId}
                            clusterData={clusterData}
                            category={cat}
                            withTopBorder={idx > 0}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ));
          })()}
        </div>
      )}
    </div>
  );
};

export default CategoryList;
